import api from './api';
import {
  Notification,
  NotificationsResponse,
  UnreadCountResponse,
  NotificationTypesResponse,
  NotificationPreferencesResponse,
  FetchNotificationsOptions,
  NotificationPreference,
} from '../types/Notification';

export const notificationApi = {
  /**
   * Get notifications for current user
   */
  getNotifications: async (options: FetchNotificationsOptions = {}): Promise<NotificationsResponse> => {
    const params = new URLSearchParams();
    
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.category) params.append('category', options.category);
    if (options.isRead !== null && options.isRead !== undefined) {
      params.append('isRead', options.isRead.toString());
    }
    if (options.isArchived) params.append('isArchived', 'true');
    if (options.viewAllBranches) params.append('viewAllBranches', 'true');
    
    const response = await api.get(`/notifications?${params.toString()}`);
    return response.data;
  },

  /**
   * Get unread notification counts (total and by category)
   */
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  /**
   * Get a single notification by ID
   */
  getNotification: async (id: number): Promise<{ success: boolean; notification: Notification }> => {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (id: number): Promise<{ success: boolean; notification: Notification }> => {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (category?: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/notifications/read-all', { category });
    return response.data;
  },

  /**
   * Archive a notification
   */
  archiveNotification: async (id: number): Promise<{ success: boolean; notification: Notification }> => {
    const response = await api.patch(`/notifications/${id}/archive`);
    return response.data;
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },

  /**
   * Get all notification types
   */
  getNotificationTypes: async (): Promise<NotificationTypesResponse> => {
    const response = await api.get('/notifications/types');
    return response.data;
  },

  /**
   * Get user notification preferences
   */
  getPreferences: async (): Promise<NotificationPreferencesResponse> => {
    const response = await api.get('/notifications/preferences');
    return response.data;
  },

  /**
   * Update user notification preferences
   */
  updatePreferences: async (
    preferences: Array<{
      notificationTypeId: number;
      inAppEnabled?: boolean;
      emailEnabled?: boolean;
      emailFrequency?: string;
    }>
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.put('/notifications/preferences', { preferences });
    return response.data;
  },

  /**
   * Update quiet hours settings
   */
  updateQuietHours: async (quietHours: {
    enabled: boolean;
    start: string | null;
    end: string | null;
    timezone: string;
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.put('/notifications/preferences/quiet-hours', quietHours);
    return response.data;
  },
};

export default notificationApi;
